import React, { useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormGroup,
  FormControlLabel,
  FormLabel,
  Checkbox,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import SaveIcon from '@mui/icons-material/Save';
import PortalLayout from '../components/PortalLayout';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const roleLabel: Record<string, string> = {
  admin: 'Administrator',
  staff: 'Mitarbeiter',
  contractor: 'Dienstleister',
  customer: 'Auftraggeber',
};

const roleColor: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'default'> = {
  admin: 'warning',
  staff: 'secondary',
  contractor: 'primary',
  customer: 'success',
};

const GESELLSCHAFTSFORMEN = [
  'GmbH', 'UG (haftungsbeschränkt)', 'AG', 'GmbH & Co. KG', 'KG', 'GbR', 'OHG',
  'E.K. (Einzelkaufmann)', 'Einzelunternehmen', 'Ltd.', 'SE', 'Sonstige',
];
const ANREDEN = ['Herr', 'Frau', 'Keine Angabe'];
const LAENDER = ['Deutschland', 'Österreich', 'Schweiz', 'EU-Ausland', 'Nicht-EU-Ausland'];
const INBOUND_TYPEN = [
  'Bestellannahme', 'Helpdesk / Technische Hotline', 'Sekretariatsservice',
  'Reservierung / Ticketing', 'Notfallservice / Notruf', 'Sonstiges',
];
const OUTBOUND_TYPEN = [
  'Adressqualifizierung', 'Kundenzufriedenheitsbefragung', 'Leadmanagement',
  'Marktforschung', 'Mystery Calls', 'Telemarketing', 'Telesales (Verkauf am Telefon)',
  'Terminvereinbarung', 'Sonstiges',
];
const SPRACHEN = ['Deutsch', 'Englisch', 'Spanisch', 'Französisch', 'Italienisch', 'Weitere Fremdsprachen'];
const VERGUETUNGSMODELLE = [
  'Auf Stundenbasis', 'Je Anruf', 'Nach Zeitaufwand', 'CPO (Cash per Order)',
  'Je Terminvereinbarung', 'Mischkalkulation / Shared Risk', 'Verhandelbar – offen für Vorschläge',
];
const SERVICEZEITEN_OPT = [
  '24 Stunden / 7 Tage pro Woche',
  'Mo.–Fr. normale Bürozeiten',
  'Sa. und/oder So.',
];
const MITARBEITER_OPT = ['1–10', '11–50', '51–200', '> 200'];

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ mt: 5, mb: 2.5 }}>
    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 1 }}>
      {children}
    </Typography>
    <Divider sx={{ borderColor: '#E85D26', borderBottomWidth: 2, width: 40 }} />
  </Box>
);

const ProfilePage: React.FC = () => {
  const { user, updateProfile: updateAuthProfile } = useAuth();
  const { addToast } = useToast();
  const isContractor = user?.role === 'contractor';

  const [profile, setProfile] = useState({
    name: '',
    phone: '',
    company_name: '',
    address: '',
    billing_address: '',
    gesellschaftsform: '',
    anrede: '',
    position: '',
    strasse: '',
    plz: '',
    ort: '',
    land: 'Deutschland',
    fax: '',
    mobil: '',
    homepage: '',
    ust_id: '',
    mitarbeiter: '',
  });

  const [servicesData, setServicesData] = useState({
    bietet_inbound: false,
    bietet_outbound: false,
    inbound_typen: [] as string[],
    outbound_typen: [] as string[],
    servicezeiten: [] as string[],
    sprachen: [] as string[],
    verguetungsmodelle: [] as string[],
  });

  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    if (user) {
      const u = user as any;
      setProfile({
        name: u.name || '',
        phone: u.phone || '',
        company_name: u.company_name || '',
        address: u.address || '',
        billing_address: u.billing_address || '',
        gesellschaftsform: u.gesellschaftsform || '',
        anrede: u.anrede || '',
        position: u.position || '',
        strasse: u.strasse || '',
        plz: u.plz || '',
        ort: u.ort || '',
        land: u.land || 'Deutschland',
        fax: u.fax || '',
        mobil: u.mobil || '',
        homepage: u.homepage || '',
        ust_id: u.ust_id || '',
        mitarbeiter: u.mitarbeiter || '',
      });
      if (u.services_data) {
        setServicesData({
          bietet_inbound: u.services_data.bietet_inbound || false,
          bietet_outbound: u.services_data.bietet_outbound || false,
          inbound_typen: u.services_data.inbound_typen || [],
          outbound_typen: u.services_data.outbound_typen || [],
          servicezeiten: u.services_data.servicezeiten || [],
          sprachen: u.services_data.sprachen || [],
          verguetungsmodelle: u.services_data.verguetungsmodelle || [],
        });
      }
    }
    setIsLoading(false);
  }, [user]);

  const handleProfileChange = (e: any) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handleServicesBoolChange = (e: any) => {
    const { name, checked } = e.target;
    setServicesData(prev => ({ ...prev, [name]: checked }));
  };

  const handleServicesArrayToggle = (field: keyof typeof servicesData, value: string) => {
    setServicesData(prev => {
      const arr = prev[field] as string[];
      return { ...prev, [field]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswords(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setPasswordError('');
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      await updateAuthProfile({
        ...profile,
        services_data: isContractor ? servicesData : undefined,
      });
      addToast('Profil erfolgreich gespeichert', 'success');
    } catch {
      addToast('Fehler beim Speichern des Profils', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwords.new_password !== passwords.confirm_password) {
      setPasswordError('Passwörter stimmen nicht überein');
      return;
    }
    if (passwords.new_password.length < 8) {
      setPasswordError('Neues Passwort muss mindestens 8 Zeichen haben');
      return;
    }
    setIsSavingPassword(true);
    try {
      await authAPI.updateProfile({
        current_password: passwords.current_password,
        new_password: passwords.new_password,
      });
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
      addToast('Passwort erfolgreich geändert', 'success');
    } catch (err: any) {
      setPasswordError(err?.response?.data?.message || 'Fehler beim Ändern des Passworts');
    } finally {
      setIsSavingPassword(false);
    }
  };

  if (isLoading) {
    return (
      <PortalLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </PortalLayout>
    );
  }

  const CheckboxGroup = ({
    label,
    options,
    field,
  }: {
    label: string;
    options: string[];
    field: keyof typeof servicesData;
  }) => (
    <FormControl component="fieldset" sx={{ width: '100%', mb: 2 }}>
      <FormLabel sx={{ fontWeight: 600, color: '#333', mb: 1, '&.Mui-focused': { color: '#333' } }}>
        {label}
      </FormLabel>
      <FormGroup row>
        {options.map(opt => (
          <FormControlLabel
            key={opt}
            control={
              <Checkbox
                checked={(servicesData[field] as string[]).includes(opt)}
                onChange={() => handleServicesArrayToggle(field, opt)}
                sx={{ '&.Mui-checked': { color: '#E85D26' } }}
              />
            }
            label={opt}
            sx={{ minWidth: { xs: '100%', sm: '48%' }, mr: 0 }}
          />
        ))}
      </FormGroup>
    </FormControl>
  );

  return (
    <PortalLayout>
      <Container maxWidth={isContractor ? 'md' : 'sm'} sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <Avatar sx={{ width: 56, height: 56, bgcolor: '#E85D26', fontSize: 24 }}>
            {profile.name ? profile.name.charAt(0).toUpperCase() : <PersonIcon />}
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {isContractor ? 'Profil bearbeiten' : 'Mein Profil'}
            </Typography>
            {user?.role && (
              <Chip label={roleLabel[user.role] || user.role} color={roleColor[user.role] || 'default'} size="small" sx={{ mt: 0.5 }} />
            )}
          </Box>
        </Box>

        <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, border: '1px solid #e0e0e0', borderRadius: 2 }}>

          {isContractor ? (
            <>
              <SectionTitle>1. Zugangsdaten</SectionTitle>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Benutzername / E-Mail-Adresse"
                    value={user?.email || ''}
                    disabled
                    helperText="Die E-Mail-Adresse kann nur durch einen Administrator geändert werden."
                    variant="outlined"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Neues Passwort (optional)"
                    name="new_password"
                    type="password"
                    value={passwords.new_password}
                    onChange={handlePasswordChange}
                    helperText="Mindestens 8 Zeichen"
                    variant="outlined"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Neues Passwort bestätigen (optional)"
                    name="confirm_password"
                    type="password"
                    value={passwords.confirm_password}
                    onChange={handlePasswordChange}
                    variant="outlined"
                  />
                </Grid>
                {passwords.new_password && (
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Aktuelles Passwort *"
                      name="current_password"
                      type="password"
                      value={passwords.current_password}
                      onChange={handlePasswordChange}
                      variant="outlined"
                    />
                  </Grid>
                )}
                {passwordError && (
                  <Grid size={12}>
                    <Alert severity="error" onClose={() => setPasswordError('')}>{passwordError}</Alert>
                  </Grid>
                )}
                {passwords.new_password && (
                  <Grid size={12}>
                    <Button
                      variant="outlined"
                      startIcon={isSavingPassword ? <CircularProgress size={16} /> : <LockIcon />}
                      onClick={handleChangePassword}
                      disabled={isSavingPassword || !passwords.current_password}
                      sx={{ borderColor: '#E85D26', color: '#E85D26', '&:hover': { borderColor: '#c94d1e', bgcolor: 'rgba(232,93,38,0.06)' } }}
                    >
                      Passwort ändern
                    </Button>
                  </Grid>
                )}
              </Grid>

              <SectionTitle>2. Kontaktdaten</SectionTitle>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel>Anrede</InputLabel>
                    <Select name="anrede" value={profile.anrede} onChange={handleProfileChange} label="Anrede">
                      {ANREDEN.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth label="Vorname *" name="name" value={profile.name.split(' ')[0] || ''} onChange={e => setProfile(prev => ({ ...prev, name: e.target.value + ' ' + prev.name.split(' ').slice(1).join(' ') }))} required variant="outlined" />
                </Grid>
                <Grid size={{ xs: 12, sm: 5 }}>
                  <TextField fullWidth label="Nachname *" name="nachname" value={profile.name.split(' ').slice(1).join(' ') || ''} onChange={e => setProfile(prev => ({ ...prev, name: (prev.name.split(' ')[0] || '') + ' ' + e.target.value }))} required variant="outlined" />
                </Grid>
                <Grid size={12}>
                  <TextField fullWidth label="Position im Unternehmen" name="position" value={profile.position} onChange={handleProfileChange} placeholder="z.B. Geschäftsführer, Vertriebsleiter" variant="outlined" />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth label="Telefon *" name="phone" value={profile.phone} onChange={handleProfileChange} required placeholder="+49 30 123456" variant="outlined" />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth label="Mobil" name="mobil" value={profile.mobil} onChange={handleProfileChange} variant="outlined" />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth label="Fax" name="fax" value={profile.fax} onChange={handleProfileChange} variant="outlined" />
                </Grid>
              </Grid>

              <SectionTitle>3. Unternehmensdaten</SectionTitle>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 8 }}>
                  <TextField fullWidth label="Firmenname *" name="company_name" value={profile.company_name} onChange={handleProfileChange} required variant="outlined" />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FormControl fullWidth>
                    <InputLabel>Gesellschaftsform</InputLabel>
                    <Select name="gesellschaftsform" value={profile.gesellschaftsform} onChange={handleProfileChange} label="Gesellschaftsform">
                      {GESELLSCHAFTSFORMEN.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={12}>
                  <TextField fullWidth label="Straße / Nr." name="strasse" value={profile.strasse} onChange={handleProfileChange} variant="outlined" />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField fullWidth label="PLZ *" name="plz" value={profile.plz} onChange={handleProfileChange} variant="outlined" />
                </Grid>
                <Grid size={{ xs: 12, sm: 5 }}>
                  <TextField fullWidth label="Ort *" name="ort" value={profile.ort} onChange={handleProfileChange} variant="outlined" />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FormControl fullWidth>
                    <InputLabel>Land *</InputLabel>
                    <Select name="land" value={profile.land} onChange={handleProfileChange} label="Land *">
                      {LAENDER.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Homepage" name="homepage" value={profile.homepage} onChange={handleProfileChange} placeholder="https://www.beispiel.de" variant="outlined" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Umsatzsteuer-ID (nur EU)" name="ust_id" value={profile.ust_id} onChange={handleProfileChange} placeholder="z.B. DE123456789" variant="outlined" />
                </Grid>
              </Grid>

              <SectionTitle>4. Angebotene Dienstleistungen</SectionTitle>

              <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                <FormLabel sx={{ fontWeight: 600, color: '#333', mb: 1, '&.Mui-focused': { color: '#333' } }}>
                  Sie bieten in Ihrem Callcenter an, Anrufe... *
                </FormLabel>
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="bietet_inbound"
                        checked={servicesData.bietet_inbound}
                        onChange={handleServicesBoolChange}
                        sx={{ '&.Mui-checked': { color: '#E85D26' } }}
                      />
                    }
                    label="Entgegenzunehmen (Inbound)"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="bietet_outbound"
                        checked={servicesData.bietet_outbound}
                        onChange={handleServicesBoolChange}
                        sx={{ '&.Mui-checked': { color: '#E85D26' } }}
                      />
                    }
                    label="Aktiv zu tätigen (Outbound / Rebound)"
                  />
                </FormGroup>
              </FormControl>

              {servicesData.bietet_inbound && (
                <Box sx={{ pl: 2, borderLeft: '3px solid #E85D26', mb: 3 }}>
                  <CheckboxGroup label="Welche Arten von Inbound bieten Sie an?" options={INBOUND_TYPEN} field="inbound_typen" />
                </Box>
              )}

              {servicesData.bietet_outbound && (
                <Box sx={{ pl: 2, borderLeft: '3px solid #533483', mb: 3 }}>
                  <CheckboxGroup label="Welche Arten von Outbound bieten Sie an?" options={OUTBOUND_TYPEN} field="outbound_typen" />
                </Box>
              )}

              <CheckboxGroup label="Zu welchen Zeiten ist Ihr Callcenter besetzt?" options={SERVICEZEITEN_OPT} field="servicezeiten" />

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Anzahl Mitarbeiter (VZÄ)</InputLabel>
                    <Select name="mitarbeiter" value={profile.mitarbeiter} onChange={handleProfileChange} label="Anzahl Mitarbeiter (VZÄ)">
                      {MITARBEITER_OPT.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <CheckboxGroup label="Welche Sprachen bieten Sie an? *" options={SPRACHEN} field="sprachen" />
              <CheckboxGroup label="Akzeptierte Vergütungsmodelle" options={VERGUETUNGSMODELLE} field="verguetungsmodelle" />
            </>
          ) : (
            <>
              <SectionTitle>Persönliche Daten</SectionTitle>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Vollständiger Name" name="name" value={profile.name} onChange={handleProfileChange} variant="outlined" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="E-Mail-Adresse" value={user?.email || ''} disabled helperText="Kann nicht geändert werden" variant="outlined" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Telefon" name="phone" value={profile.phone} onChange={handleProfileChange} placeholder="+49 123 456789" variant="outlined" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Firmenname" name="company_name" value={profile.company_name} onChange={handleProfileChange} variant="outlined" />
                </Grid>
              </Grid>

              <SectionTitle>Passwort ändern</SectionTitle>
              {passwordError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPasswordError('')}>{passwordError}</Alert>}
              <Grid container spacing={2}>
                <Grid size={12}>
                  <TextField fullWidth label="Aktuelles Passwort" name="current_password" type="password" value={passwords.current_password} onChange={handlePasswordChange} variant="outlined" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Neues Passwort" name="new_password" type="password" value={passwords.new_password} onChange={handlePasswordChange} helperText="Mindestens 8 Zeichen" variant="outlined" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Neues Passwort bestätigen" name="confirm_password" type="password" value={passwords.confirm_password} onChange={handlePasswordChange} variant="outlined" />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={isSavingPassword ? <CircularProgress size={16} /> : <LockIcon />}
                  onClick={handleChangePassword}
                  disabled={isSavingPassword || !passwords.current_password || !passwords.new_password}
                  sx={{ borderColor: '#E85D26', color: '#E85D26', '&:hover': { borderColor: '#c94d1e', bgcolor: 'rgba(232,93,38,0.06)' } }}
                >
                  Passwort ändern
                </Button>
              </Box>
            </>
          )}

          <Divider sx={{ my: 4 }} />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveProfile}
              disabled={isSaving}
              sx={{
                background: 'linear-gradient(135deg, #E85D26 0%, #c94d1e 100%)',
                fontWeight: 700,
                px: 4,
                '&:hover': { background: 'linear-gradient(135deg, #d94f1e 0%, #b8431a 100%)' },
              }}
            >
              Speichern
            </Button>
          </Box>
        </Paper>
      </Container>
    </PortalLayout>
  );
};

export default ProfilePage;
